import React, { useState, useRef } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Link } from 'react-router-dom';

const WaiverForm = ({ onAccept, errors }) => {
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [parqAccepted, setParqAccepted] = useState(false);
  const [signature, setSignature] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef(null);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef?.current;
    const rect = canvas?.getBoundingClientRect();
    const x = (e?.clientX || e?.touches?.[0]?.clientX) - rect?.left;
    const y = (e?.clientY || e?.touches?.[0]?.clientY) - rect?.top;
    setLastPos({ x, y });
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');
    const rect = canvas?.getBoundingClientRect();
    const x = (e?.clientX || e?.touches?.[0]?.clientX) - rect?.left;
    const y = (e?.clientY || e?.touches?.[0]?.clientY) - rect?.top;

    ctx.strokeStyle = '#0F172A';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx?.beginPath();
    ctx?.moveTo(lastPos?.x, lastPos?.y);
    ctx?.lineTo(x, y);
    ctx?.stroke();

    setLastPos({ x, y });
    setSignature('signed');
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');
    ctx?.clearRect(0, 0, canvas?.width, canvas?.height);
    setSignature('');
  };

  const handleAccept = () => {
    if (waiverAccepted && parqAccepted && signature) {
      onAccept({ waiverAccepted, parqAccepted, signature });
    }
  };

  return (
    <div className="bg-card rounded-xl p-6 md:p-8 shadow-md border border-border">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="FileText" size={20} color="var(--color-primary)" />
        </div>
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
          Waivers & Agreements
        </h2>
      </div>
      <div className="space-y-6 md:space-y-8">
        <div className="bg-muted rounded-lg p-4 md:p-6 max-h-64 overflow-y-auto custom-scrollbar">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Liability Waiver
          </h3>
          <div className="text-sm text-foreground space-y-3">
            <p>
              I acknowledge that participation in fitness activities involves inherent risks, including but not limited to physical injury, illness, or death. I voluntarily assume all such risks.
            </p>
            <p>
              I hereby release, waive, and discharge TrainaryGym, its owners, employees, trainers, and agents from any and all liability, claims, demands, or causes of action arising from my participation in gym activities.
            </p>
            <p>
              I certify that I am in good physical condition and have no medical conditions that would prevent my safe participation in fitness activities. I agree to consult with a physician before beginning any exercise program.
            </p>
            <p>
              I understand that TrainaryGym reserves the right to refuse service or terminate membership for violation of gym policies, including but not limited to inappropriate behavior, equipment misuse, or non-payment.
            </p>
          </div>
        </div>

        <Checkbox
          label="I have read and agree to the Liability Waiver"
          checked={waiverAccepted}
          onChange={(e) => setWaiverAccepted(e?.target?.checked)}
          error={errors?.waiverAccepted}
          required
        />

        <div className="bg-muted rounded-lg p-4 md:p-6 max-h-64 overflow-y-auto custom-scrollbar">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            PAR-Q (Physical Activity Readiness Questionnaire)
          </h3>
          <div className="text-sm text-foreground space-y-3">
            <p className="font-medium">
              Please answer the following questions honestly:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Has your doctor ever said you have a heart condition?</li>
              <li>Do you feel pain in your chest during physical activity?</li>
              <li>Have you experienced chest pain when not doing physical activity?</li>
              <li>Do you lose balance due to dizziness or lose consciousness?</li>
              <li>Do you have bone or joint problems that could worsen with activity?</li>
              <li>Are you currently taking medication for blood pressure or heart condition?</li>
              <li>Do you know of any other reason you should not engage in physical activity?</li>
            </ul>
            <p className="mt-4">
              If you answered YES to any question, consult your physician before increasing physical activity. If you answered NO to all questions, you can be reasonably sure you can safely participate.
            </p>
          </div>
        </div>

        <Checkbox
          label="I have completed the PAR-Q and confirm I am fit to participate"
          checked={parqAccepted}
          onChange={(e) => setParqAccepted(e?.target?.checked)}
          error={errors?.parqAccepted}
          required
        />

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Electronic Signature <span className="text-error">*</span>
          </label>
          <div className="border-2 border-border rounded-lg overflow-hidden">
            <canvas
              ref={canvasRef}
              width={600}
              height={200}
              className="w-full h-48 bg-background cursor-crosshair touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-muted-foreground">
              Sign above using your mouse or finger
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSignature}
              iconName="RotateCcw"
              iconPosition="left"
            >
              Clear
            </Button>
          </div>
          {errors?.signature && (
            <p className="text-sm text-error mt-2">{errors?.signature}</p>
          )}
        </div>

        <div className="bg-primary/5 rounded-lg p-4 flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground">Legal Agreement</p>
            <p className="text-xs text-muted-foreground mt-1">
              By signing above, you acknowledge that you have read, understood, and agree to all terms and conditions outlined in the waivers and questionnaires.
            </p>
            <div className="flex items-start gap-3">
              <Checkbox
                name="waiverAccepted"
                checked={waiverAccepted}
                onChange={(e) => setWaiverAccepted(e?.target?.checked)}
              />
              <label className="text-sm text-muted-foreground cursor-pointer" onClick={() => setWaiverAccepted(!waiverAccepted)}>
                I have read and agree to the{' '}
                <Link to="/terms-and-conditions" className="text-primary hover:text-primary/80 transition-colors duration-250 focus-ring rounded px-1" target="_blank">
                  Terms and Conditions
                </Link>
                {' '}and{' '}
                <Link to="/privacy-policy" className="text-primary hover:text-primary/80 transition-colors duration-250 focus-ring rounded px-1" target="_blank">
                  Privacy Policy
                </Link>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaiverForm;